import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Divider,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { blue, grey, green, orange, red, purple } from "@mui/material/colors";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { allPOItemAssoc, poItemStyleAssoc } from "data/pimData";
import { getAllPo } from "../fobPoApi";

const money = (value) => {
	const v = Number(value) || 0;
	if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
	if (v >= 100000) return `₹${(v / 100000).toFixed(1)} L`;
	return `₹${v.toLocaleString("en-IN")}`;
};

const fmtDate = (value) => (value ? moment(value).format("DD MMM YYYY") : "--");

const statusColors = {
	DRAFTED: { bg: grey[200], color: grey[900] },
	NEW: { bg: blue[100], color: blue[900] },
	PUBLISHED: { bg: green[100], color: green[900] },
	REJECTED: { bg: red[100], color: red[900] },
};

const sections = [
	{ key: "allocation", label: "Vendor Allocation" },
	{ key: "po", label: "Generate PO" },
	{ key: "tracking", label: "Production Tracking" },
	{ key: "reporting", label: "Reporting" },
	{ key: "intelligence", label: "AI Insights" },
];

const groupBy = (arr, getKey) =>
	arr.reduce((acc, item) => {
		const key = getKey(item);
		if (!acc[key]) acc[key] = [];
		acc[key].push(item);
		return acc;
	}, {});

const flattenPoItems = (rows) => {
	return rows.flatMap((po) =>
		(po?.[allPOItemAssoc] || []).map((item) => ({
			...item,
			poId: po.id,
			order_no: po.order_no,
			date: po.date,
			channel: po.channel,
			owner_site: po.owner_site,
			vendor_name: po.vendor_name,
			purchase_type: po.purchase_type,
			delivery_date: po.delivery_date,
			status: po.status || (po.is_published ? "PUBLISHED" : "DRAFTED"),
			stage: po.stage || (po.is_published ? "PUBLISHED" : "DRAFTED"),
			style_id: item.style_id,
			style_name: item?.[poItemStyleAssoc]?.style_name || "--",
			brand: item?.[poItemStyleAssoc]?.brand || "--",
			category: item?.[poItemStyleAssoc]?.category || "--",
			size_type: item?.[poItemStyleAssoc]?.size_type || "--",
			mrp: item?.[poItemStyleAssoc]?.mrp || 0,
			total_qty: Number(item?.total_qty) || 0,
			prodType: po.purchase_type || "FOB",
			delayRisk: item?.[poItemStyleAssoc]?.delayRisk || po.delayRisk || 0,
		})),
	);
};

const FOBReport = () => {
	const [poRows, setPoRows] = useState([]);
	const [activeSection, setActiveSection] = useState("allocation");
	const [query, setQuery] = useState("");
	const [filters, setFilters] = useState({
		brand: "",
		category: "",
		prodType: "",
		vendor: "",
		status: "",
		drop: "",
	});

	useEffect(() => {
		getAllPo().then((rows) => setPoRows(rows || []));
	}, []);

	const items = useMemo(() => flattenPoItems(poRows), [poRows]);

	const vendors = useMemo(() => {
		const vendorGroups = groupBy(items, (row) => row.vendor_name || "Unknown");
		return Object.entries(vendorGroups)
			.map(([name, rows]) => ({
				name,
				count: rows.length,
				value: rows.reduce((acc, row) => acc + row.total_qty * (Number(row.mrp) || 0), 0),
				styles: [...new Set(rows.map((row) => row.style_id))].length,
				avgRisk: Math.round(rows.reduce((acc, row) => acc + (Number(row.delayRisk) || 0), 0) / rows.length),
			}))
			.sort((a, b) => b.value - a.value);
	}, [items]);

	const styles = useMemo(() => {
		const styleGroups = groupBy(items, (row) => row.style_id || row.style_name || "--");
		return Object.entries(styleGroups)
			.map(([styleId, rows]) => {
				const first = rows[0];
				const qty = rows.reduce((acc, row) => acc + row.total_qty, 0);
				const value = rows.reduce((acc, row) => acc + row.total_qty * (Number(row.mrp) || 0), 0);
				const vendorSet = [...new Set(rows.map((row) => row.vendor_name))];
				const maxRisk = Math.max(...rows.map((row) => Number(row.delayRisk) || 0));
				return {
					styleId,
					code: first?.style_id || styleId,
					name: first?.style_name || "--",
					brand: first?.brand || "--",
					category: first?.category || "--",
					vendor: vendorSet[0] || "--",
					qty,
					value,
					status: first?.status || "DRAFTED",
					prodType: first?.prodType || "FOB",
					delivery_date: first?.delivery_date,
					delayRisk: maxRisk,
					poNo: first?.order_no || "Pending",
				};
			})
			.sort((a, b) => b.value - a.value);
	}, [items]);

	const filteredItems = useMemo(() => {
		const q = query.trim().toLowerCase();
		return styles.filter((row) => {
			const matchesQuery =
				!q ||
				[row.code, row.name, row.vendor, row.brand, row.category, row.poNo]
					.filter(Boolean)
					.join(" ")
					.toLowerCase()
					.includes(q);
			const matchesBrand = !filters.brand || row.brand === filters.brand;
			const matchesCategory = !filters.category || row.category === filters.category;
			const matchesProdType = !filters.prodType || row.prodType === filters.prodType;
			const matchesVendor = !filters.vendor || row.vendor === filters.vendor;
			const matchesStatus = !filters.status || row.status === filters.status;
			const matchesDrop =
				!filters.drop || moment(row.delivery_date).diff(moment(), "days") <= Number(filters.drop);
			return matchesQuery && matchesBrand && matchesCategory && matchesProdType && matchesVendor && matchesStatus && matchesDrop;
		});
	}, [styles, query, filters]);

	const kpis = useMemo(() => {
		const value = filteredItems.reduce((acc, row) => acc + row.value, 0);
		const poDone = filteredItems.filter((row) => row.poNo !== "Pending").length;
		const delayed = filteredItems.filter((row) => row.delayRisk > 72).length;
		const upcoming = filteredItems.filter((row) => moment(row.delivery_date).diff(moment(), "days") <= 30).length;
		const avgRisk = filteredItems.length
			? Math.round(filteredItems.reduce((acc, row) => acc + (Number(row.delayRisk) || 0), 0) / filteredItems.length)
			: 0;
		return [
			{ label: "Styles", value: filteredItems.length, sub: "filtered items" },
			{ label: "Docs Done", value: poDone, sub: `${filteredItems.length ? Math.round((poDone / filteredItems.length) * 100) : 0}% conversion` },
			{ label: "Value", value: money(value), sub: `${filteredItems.reduce((acc, row) => acc + row.qty, 0).toLocaleString("en-IN")} units` },
			{ label: "Drops 30D", value: upcoming, sub: "next 30 days" },
			{ label: "Delay Risk", value: delayed, sub: "needs follow-up", tone: "risk" },
			{ label: "Avg Risk", value: `${avgRisk}%`, sub: "estimated" },
		];
	}, [filteredItems]);

	const reportRows = useMemo(() => {
		const vendorGroups = groupBy(filteredItems, (row) => row.vendor || "Unknown");
		const categoryGroups = groupBy(filteredItems, (row) => row.category || "Unknown");
		return {
			vendorGroups,
			categoryGroups,
		};
	}, [filteredItems]);

	const currentPoDraft = filteredItems[0];

	return (
		<Box sx={{ width: "100%", pb: 2, bgcolor: "#fafaf8", color: "#111110" }}>
			<style>{`
				.fob-shell {
					width: 100%;
				}
				.fob-hero {
					background: #fff;
					border: 1px solid rgba(0, 0, 0, 0.08);
					border-radius: 14px;
					padding: 18px;
					box-shadow: 0 8px 30px rgba(0, 0, 0, 0.05);
					margin-bottom: 14px;
				}
				.fob-title {
					font-size: 30px;
					line-height: 1.1;
					font-weight: 700;
					color: #111110;
				}
				.fob-subtitle {
					font-size: 12px;
					color: #6b6b68;
					margin-top: 4px;
				}
				.fob-actions {
					display: flex;
					gap: 8px;
					flex-wrap: wrap;
				}
				.fob-pill {
					border-radius: 22px !important;
					padding: 7px 14px !important;
					font-size: 11px !important;
					text-transform: none !important;
				}
				.fob-pill-primary {
					background: #111110 !important;
					color: #fff !important;
				}
				.fob-pill-gold {
					background: #c8a96e !important;
					color: #111110 !important;
				}
				.fob-filterbar {
					display: grid;
					grid-template-columns: 1.2fr repeat(6, minmax(110px, 1fr)) auto;
					gap: 8px;
					align-items: center;
					background: #fff;
					border: 1px solid rgba(0, 0, 0, 0.08);
					border-radius: 14px;
					padding: 12px;
					box-shadow: 0 8px 30px rgba(0, 0, 0, 0.05);
					margin-bottom: 14px;
				}
				.fob-kpis {
					display: grid;
					grid-template-columns: repeat(6, 1fr);
					gap: 10px;
					margin-bottom: 14px;
				}
				.fob-kpi {
					background: #fff;
					border: 1px solid rgba(0, 0, 0, 0.08);
					border-radius: 14px;
					padding: 14px 15px;
					box-shadow: 0 8px 30px rgba(0, 0, 0, 0.05);
				}
				.fob-k-label {
					font-size: 9px;
					text-transform: uppercase;
					letter-spacing: 0.08em;
					color: #6b6b68;
					margin-bottom: 7px;
				}
				.fob-k-val {
					font-size: 25px;
					line-height: 1;
					font-weight: 700;
					color: #111110;
				}
				.fob-k-sub {
					font-size: 10px;
					color: #9b9b97;
					margin-top: 5px;
				}
				.fob-tabs {
					display: flex;
					gap: 6px;
					border-bottom: 1px solid rgba(0, 0, 0, 0.08);
					padding-bottom: 10px;
					margin-bottom: 14px;
					flex-wrap: wrap;
				}
				.fob-tab {
					font-size: 11px;
					padding: 7px 12px;
					border-radius: 8px;
					color: #6b6b68;
					cursor: pointer;
					background: transparent;
					border: 0;
				}
				.fob-tab.active {
					background: #111110;
					color: #fff;
				}
				.fob-panel {
					background: #fff;
					border: 1px solid rgba(0, 0, 0, 0.08);
					border-radius: 14px;
					padding: 16px 18px;
					box-shadow: 0 8px 30px rgba(0, 0, 0, 0.05);
				}
				.fob-grid-2 {
					display: grid;
					grid-template-columns: 1fr 1fr;
					gap: 14px;
					margin-bottom: 14px;
				}
				.fob-grid-3 {
					display: grid;
					grid-template-columns: 1fr 1fr 1fr;
					gap: 14px;
					margin-bottom: 14px;
				}
				.fob-idea-grid {
					display: grid;
					grid-template-columns: repeat(3, 1fr);
					gap: 14px;
				}
				.fob-idea {
					border-left: 3px solid #c8a96e;
					border-radius: 12px;
					border-top: 1px solid rgba(0, 0, 0, 0.08);
					border-right: 1px solid rgba(0, 0, 0, 0.08);
					border-bottom: 1px solid rgba(0, 0, 0, 0.08);
					padding: 15px;
					background: #fff;
				}
				.fob-idea h3 {
					font-size: 13px;
					margin-bottom: 5px;
				}
				.fob-idea p {
					font-size: 11px;
					color: #6b6b68;
					line-height: 1.55;
				}
				.fob-report-table {
					width: 100%;
					border-collapse: collapse;
				}
				.fob-report-table thead th {
					font-size: 10px;
					color: rgba(0, 0, 0, 0.45);
					font-weight: 600;
					text-align: left;
					padding: 0 8px 10px;
					border-bottom: 1px solid rgba(0, 0, 0, 0.08);
					white-space: nowrap;
				}
				.fob-report-table thead th.num,
				.fob-report-table td.num {
					text-align: right;
				}
				.fob-report-table td {
					font-size: 11px;
					color: rgba(0, 0, 0, 0.78);
					padding: 10px 8px;
					border-bottom: 1px solid rgba(0, 0, 0, 0.08);
					vertical-align: middle;
				}
				.fob-report-table tbody tr:hover td {
					background: rgba(0, 0, 0, 0.02);
				}
				.fob-report-table td strong {
					font-weight: 700;
					color: rgba(0, 0, 0, 0.92);
				}
				@media (max-width: 1100px) {
					.fob-filterbar {
						grid-template-columns: 1fr 1fr;
					}
					.fob-kpis {
						grid-template-columns: repeat(2, 1fr);
					}
					.fob-grid-2,
					.fob-grid-3,
					.fob-idea-grid {
						grid-template-columns: 1fr;
					}
				}
			`}</style>
			<Box className="fob-shell">
				<Box className="fob-hero">
					<Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
					<Box>
						<Typography className="fob-title">
							Production Allocation & Tracking
						</Typography>
						<Typography className="fob-subtitle">
							Vendor allocation, PO generation, tracking, and reporting for FOB purchase orders.
						</Typography>
					</Box>
						<Box className="fob-actions">
							<Button className="fob-pill" variant="outlined">
								Import Designs
							</Button>
							<Button className="fob-pill" variant="outlined">
								Export Report
							</Button>
							<Button className="fob-pill fob-pill-primary" variant="contained">
								Generate PO / JO
							</Button>
						</Box>
					</Box>

					<div className="fob-filterbar">
						<TextField
							size="small"
							fullWidth
							placeholder="Search style, vendor, PO..."
							value={query}
							onChange={(e) => setQuery(e.target.value)}
						/>
						<Select size="small" displayEmpty value={filters.brand} onChange={(e) => setFilters((prev) => ({ ...prev, brand: e.target.value }))}>
							<MenuItem value="">All brands</MenuItem>
							{[...new Set(styles.map((row) => row.brand).filter(Boolean))].map((brand) => (
								<MenuItem key={brand} value={brand}>
									{brand}
								</MenuItem>
							))}
						</Select>
						<Select size="small" displayEmpty value={filters.category} onChange={(e) => setFilters((prev) => ({ ...prev, category: e.target.value }))}>
							<MenuItem value="">All categories</MenuItem>
							{[...new Set(styles.map((row) => row.category).filter(Boolean))].map((category) => (
								<MenuItem key={category} value={category}>
									{category}
								</MenuItem>
							))}
						</Select>
						<Select size="small" displayEmpty value={filters.prodType} onChange={(e) => setFilters((prev) => ({ ...prev, prodType: e.target.value }))}>
							<MenuItem value="">All types</MenuItem>
							<MenuItem value="FOB">FOB</MenuItem>
							<MenuItem value="Job Work">Job Work</MenuItem>
							<MenuItem value="Own Production">Own Production</MenuItem>
						</Select>
						<Select size="small" displayEmpty value={filters.vendor} onChange={(e) => setFilters((prev) => ({ ...prev, vendor: e.target.value }))}>
							<MenuItem value="">All vendors</MenuItem>
							{vendors.map((vendor) => (
								<MenuItem key={vendor.name} value={vendor.name}>
									{vendor.name}
								</MenuItem>
							))}
						</Select>
						<Select size="small" displayEmpty value={filters.status} onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}>
							<MenuItem value="">All statuses</MenuItem>
							<MenuItem value="NEW">New</MenuItem>
							<MenuItem value="DRAFTED">Drafted</MenuItem>
							<MenuItem value="PUBLISHED">Published</MenuItem>
						</Select>
						<Select size="small" displayEmpty value={filters.drop} onChange={(e) => setFilters((prev) => ({ ...prev, drop: e.target.value }))}>
							<MenuItem value="">All drops</MenuItem>
							<MenuItem value="15">Next 15 days</MenuItem>
							<MenuItem value="30">Next 30 days</MenuItem>
							<MenuItem value="45">Next 45 days</MenuItem>
						</Select>
						<Button className="fob-pill fob-pill-gold" variant="contained" onClick={() => setFilters({ brand: "", category: "", prodType: "", vendor: "", status: "", drop: "" })}>
							Reset
						</Button>
					</div>
				</Box>

			<div className="fob-kpis">
				{kpis.map((item) => (
					<div className="fob-kpi" key={item.label}>
						<div className="fob-k-label">{item.label}</div>
						<div className="fob-k-val">{item.value}</div>
						<div className="fob-k-sub">{item.sub}</div>
					</div>
				))}
			</div>

			<div className="fob-tabs">
				{sections.map((section) => (
					<button key={section.key} className={`fob-tab ${activeSection === section.key ? "active" : ""}`} onClick={() => setActiveSection(section.key)}>
						{section.label}
					</button>
				))}
			</div>

			{activeSection === "allocation" && (
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.15fr 0.85fr" }, gap: 2 }}>
					<div className="fob-panel">
						<Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: "#111110" }}>
							Style-wise vendor allocation
						</Typography>
						<Box sx={{ overflowX: "auto" }}>
							<table className="fob-report-table">
								<thead>
									<tr>
										<th>Style</th>
										<th>Type</th>
										<th>Brand</th>
										<th>Category</th>
										<th className="num">Qty</th>
										<th className="num">Value</th>
										<th>Status</th>
										<th>Drop</th>
									</tr>
								</thead>
								<tbody>
									{filteredItems.slice(0, 12).map((row) => (
										<tr key={`${row.styleId}-${row.vendor}`}>
											<td>
												<strong>{row.code}</strong>
												<br />
												<span style={{ color: grey[600], fontSize: 10 }}>{row.name}</span>
											</td>
											<td>
												<Chip
													size="small"
													label={row.prodType}
													sx={{
														bgcolor: row.prodType === "FOB" ? blue[50] : row.prodType === "Job Work" ? orange[50] : green[50],
														color: row.prodType === "FOB" ? blue[900] : row.prodType === "Job Work" ? orange[900] : green[900],
													}}
												/>
											</td>
											<td>{row.brand}</td>
											<td>{row.category}</td>
											<td className="num">{row.qty.toLocaleString("en-IN")}</td>
											<td className="num">{money(row.value)}</td>
											<td>
												<Chip
													size="small"
													label={row.status}
													sx={{
														bgcolor: statusColors[row.status]?.bg || grey[200],
														color: statusColors[row.status]?.color || grey[900],
													}}
												/>
											</td>
											<td>{fmtDate(row.delivery_date)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</Box>
					</div>

					<div className="fob-panel">
						<Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: "#111110" }}>
							Recommended vendors
						</Typography>
						<Stack spacing={1.25}>
							{vendors.slice(0, 5).map((vendor, index) => (
								<Card key={vendor.name} variant="outlined" sx={{ borderRadius: 3 }}>
									<CardContent sx={{ p: 1.5 }}>
										<Box display="flex" justifyContent="space-between" alignItems="flex-start">
											<Box>
												<Typography variant="subtitle2" fontWeight={700}>
													{index + 1}. {vendor.name}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{vendor.styles} styles · {vendor.count} line items
												</Typography>
											</Box>
											<Chip size="small" label={`${vendor.avgRisk}% risk`} color={vendor.avgRisk > 72 ? "error" : vendor.avgRisk > 45 ? "warning" : "success"} />
										</Box>
										<Box sx={{ mt: 1 }}>
											<Typography variant="body2" fontWeight={700} color="text.primary">
												{money(vendor.value)}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												Estimated allocation value
											</Typography>
										</Box>
									</CardContent>
								</Card>
							))}
						</Stack>
					</div>
				</Box>
			)}

			{activeSection === "po" && (
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.15fr 0.85fr" }, gap: 2 }}>
					<div className="fob-panel">
						<Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: "#111110" }}>
							PO / Job Order draft
						</Typography>
						<Box sx={{ p: 2, borderRadius: 2, border: "1px solid rgba(0,0,0,.08)", mb: 2 }}>
							<Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
								<Box>
									<Typography variant="subtitle1" fontWeight={800}>
										{poRows[0]?.vendor_name || "Vendor"}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Reg. Office: FOB dashboard draft
									</Typography>
								</Box>
								<Box textAlign="right">
									<Typography variant="h6" fontWeight={700}>
										{currentPoDraft?.purchase_type === "Job Work" ? "Job Order" : "Purchase Order"}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										Order No: {poRows[0]?.order_no || "Pending"}<br />
										Date: {fmtDate(poRows[0]?.date)}
									</Typography>
								</Box>
							</Box>
							<Divider sx={{ my: 2 }} />
							<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
								<Card variant="outlined" sx={{ borderRadius: 2 }}>
									<CardContent>
										<Typography variant="overline" color="text.secondary">
											Vendor
										</Typography>
										<Typography variant="body2">{poRows[0]?.vendor_name || "--"}</Typography>
									</CardContent>
								</Card>
								<Card variant="outlined" sx={{ borderRadius: 2 }}>
									<CardContent>
										<Typography variant="overline" color="text.secondary">
											Delivery
										</Typography>
										<Typography variant="body2">{fmtDate(poRows[0]?.delivery_date)}</Typography>
									</CardContent>
								</Card>
							</Box>
							<Box sx={{ mt: 2, overflowX: "auto" }}>
								<table className="fob-report-table">
									<thead>
										<tr>
											<th>Style</th>
											<th>Category</th>
											<th className="num">Qty</th>
											<th className="num">MRP</th>
											<th className="num">Value</th>
										</tr>
									</thead>
									<tbody>
										{items.slice(0, 8).map((row, index) => (
											<tr key={`${row.style_id}-${index}`}>
												<td>{row.code || row.style_name}</td>
												<td>{row.category}</td>
												<td className="num">{row.total_qty.toLocaleString("en-IN")}</td>
												<td className="num">{money(row.mrp)}</td>
												<td className="num">{money(row.total_qty * (Number(row.mrp) || 0))}</td>
											</tr>
										))}
									</tbody>
								</table>
							</Box>
						</Box>
					</div>

					<div className="fob-panel">
						<Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: "#111110" }}>
							PO controls
						</Typography>
						<Stack spacing={1}>
							{[
								["Draft", poRows.filter((row) => !row.is_published).length, purple[100], purple[900]],
								["Approval", poRows.filter((row) => row.status === "NEW").length, orange[100], orange[900]],
								["Released", poRows.filter((row) => row.is_published).length, green[100], green[900]],
								["In Production", items.filter((row) => row.prodType !== "Own Production").length, blue[100], blue[900]],
								["Received", items.filter((row) => row.status === "PUBLISHED").length, green[100], green[900]],
							].map(([label, value, bg, color]) => (
								<Card key={label} variant="outlined" sx={{ borderRadius: 3 }}>
									<CardContent sx={{ p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
										<Box>
											<Typography variant="subtitle2" fontWeight={700}>
												{label}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												Workflow summary
											</Typography>
										</Box>
										<Chip label={value} sx={{ bgcolor: bg, color }} />
									</CardContent>
								</Card>
							))}
						</Stack>
					</div>
				</Box>
			)}

			{activeSection === "tracking" && (
				<>
					<div className="fob-grid-3">
						<div className="fob-panel">
							<Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: "#111110" }}>
								Production stage funnel
							</Typography>
							<Box sx={{ height: 260 }}>
								<Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, height: "100%" }}>
									{["Design Created", "PO Done", "GRN Done"].map((label, idx) => (
										<Box key={label} sx={{ border: "1px solid rgba(0,0,0,.08)", borderRadius: 2, p: 1.5, bgcolor: "#fafaf8" }}>
											<Typography variant="caption" sx={{ color: "#6b6b68", textTransform: "uppercase", letterSpacing: ".08em" }}>
												{label}
											</Typography>
											<Typography variant="h4" sx={{ fontWeight: 700, color: "#111110", mt: 0.5 }}>
												{[filteredItems.length, filteredItems.filter((row) => row.poNo !== "Pending").length, filteredItems.filter((row) => row.status === "PUBLISHED").length][idx]}
											</Typography>
										</Box>
									))}
								</Box>
							</Box>
						</div>
						<div className="fob-panel">
							<Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: "#111110" }}>
								Expected drop calendar
							</Typography>
							<table className="fob-report-table">
								<thead>
									<tr>
										<th>Drop Date</th>
										<th className="num">Styles</th>
										<th className="num">Value</th>
									</tr>
								</thead>
								<tbody>
									{Object.entries(groupBy(filteredItems, (row) => fmtDate(row.delivery_date)))
										.slice(0, 8)
										.map(([date, rows]) => (
											<tr key={date}>
												<td>{date}</td>
												<td className="num">{rows.length}</td>
												<td className="num">{money(rows.reduce((acc, row) => acc + row.value, 0))}</td>
											</tr>
										))}
								</tbody>
							</table>
						</div>
						<div className="fob-panel">
							<Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: "#111110" }}>
								Delay risk radar
							</Typography>
							<Box sx={{ display: "grid", gap: 1 }}>
								{[
									["Low", filteredItems.filter((row) => row.delayRisk <= 45).length, "#16784f", "#eaf4ee"],
									["Medium", filteredItems.filter((row) => row.delayRisk > 45 && row.delayRisk <= 72).length, "#b07a20", "#fdf3e0"],
									["High", filteredItems.filter((row) => row.delayRisk > 72).length, "#b83232", "#fdeaea"],
								].map(([label, count, color, bg]) => (
									<Box key={label} sx={{ p: 1.5, borderRadius: 2, bgcolor: bg, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
										<Typography variant="subtitle2" sx={{ color, fontWeight: 700 }}>
											{label}
										</Typography>
										<Typography variant="h5" sx={{ color, fontWeight: 700 }}>
											{count}
										</Typography>
									</Box>
								))}
							</Box>
						</div>
					</div>
					<div className="fob-panel">
						<Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: "#111110" }}>
							Style production tracker
						</Typography>
						<Box sx={{ overflowX: "auto" }}>
							<table className="fob-report-table">
								<thead>
									<tr>
										<th>Style</th>
										<th>Type</th>
										<th>Vendor</th>
										<th>Document</th>
										<th>Status</th>
										<th>Delivery</th>
										<th className="num">Delay Risk</th>
									</tr>
								</thead>
								<tbody>
									{filteredItems.map((row) => (
										<tr key={`${row.styleId}-${row.poId}`}>
											<td>
												<strong>{row.code}</strong>
												<br />
												<span style={{ color: grey[600], fontSize: 10 }}>{row.name}</span>
											</td>
											<td>{row.prodType}</td>
											<td>{row.vendor}</td>
											<td>{row.poNo}</td>
											<td>{row.stage}</td>
											<td>{fmtDate(row.delivery_date)}</td>
											<td className="num">
												<Chip size="small" label={`${row.delayRisk}%`} color={row.delayRisk > 72 ? "error" : row.delayRisk > 45 ? "warning" : "success"} />
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</Box>
					</div>
				</>
			)}

			{activeSection === "reporting" && (
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
					<div className="fob-panel">
						<Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: "#111110" }}>
							Vendor-wise summary
						</Typography>
						<table className="fob-report-table">
							<thead>
								<tr>
									<th>Vendor</th>
									<th className="num">Styles</th>
									<th className="num">Value</th>
								</tr>
							</thead>
							<tbody>
								{Object.entries(reportRows.vendorGroups)
									.map(([vendor, rows]) => ({
										vendor,
										styles: new Set(rows.map((row) => row.style_id)).size,
										value: rows.reduce((acc, row) => acc + row.total_qty * (Number(row.mrp) || 0), 0),
									}))
									.sort((a, b) => b.value - a.value)
									.slice(0, 8)
									.map((row) => (
										<tr key={row.vendor}>
											<td>{row.vendor}</td>
											<td className="num">{row.styles}</td>
											<td className="num">{money(row.value)}</td>
										</tr>
									))}
							</tbody>
						</table>
					</div>

					<div className="fob-panel">
						<Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: "#111110" }}>
							Category production value
						</Typography>
						<table className="fob-report-table">
							<thead>
								<tr>
									<th>Category</th>
									<th className="num">Styles</th>
									<th className="num">Value</th>
								</tr>
							</thead>
							<tbody>
								{Object.entries(reportRows.categoryGroups)
									.map(([category, rows]) => ({
										category,
										styles: new Set(rows.map((row) => row.style_id)).size,
										value: rows.reduce((acc, row) => acc + row.total_qty * (Number(row.mrp) || 0), 0),
									}))
									.sort((a, b) => b.value - a.value)
									.slice(0, 8)
									.map((row) => (
										<tr key={row.category}>
											<td>{row.category}</td>
											<td className="num">{row.styles}</td>
											<td className="num">{money(row.value)}</td>
										</tr>
									))}
							</tbody>
						</table>
					</div>
				</Box>
			)}

			{activeSection === "intelligence" && (
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "repeat(3, 1fr)" }, gap: 2 }}>
					{[
						{
							title: "Production risk escalation",
							desc: `${filteredItems.filter((row) => row.delayRisk > 72).length} styles are above high-risk threshold. Escalate vendor follow-up and move QC earlier.`,
							tone: red[700],
						},
						{
							title: "Document conversion gap",
							desc: `${poRows.filter((row) => !row.is_published).length} PO drafts are still pending. Generate final docs for approved styles.`,
							tone: purple[700],
						},
						{
							title: "Vendor capacity optimization",
							desc: `Keep the highest value vendor under control by splitting future allocations when a vendor crosses 35% of seasonal value.`,
							tone: green[700],
						},
						{
							title: "Drop readiness check",
							desc: `Confirm fabric inhouse, packaging, and dispatch readiness 7 days before the earliest delivery date in this filter set.`,
							tone: blue[700],
						},
						{
							title: "Category concentration",
							desc: `Use category filters to monitor value concentration across categories before final PO release.`,
							tone: orange[700],
						},
						{
							title: "Split vendor strategy",
							desc: `For high quantity styles, consider a 70:30 allocation split between primary and backup vendors.`,
							tone: "#a16207",
						},
					].map((item) => (
						<div key={item.title} className="fob-idea" style={{ borderLeftColor: item.tone }}>
							<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
								{item.title}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{item.desc}
							</Typography>
						</div>
					))}
				</Box>
			)}
		</Box>
		</Box>
	);
};

export default FOBReport;
